import { computed, observable, runInAction } from "mobx"

import { fetchGetStudentInputFlowJudgeEntered } from "~/api/student/input-flow/judge_entered/get"
import { RoutingInfo, InputProfileFlowKey, Routing } from "~/components/pages/InputProfileFlowPages/types"
import { JudgeEntered } from "~/entities/InputProfileFlow/JudgeEntered"

export default class InputProfileFlowStore {
  @observable judgeEntered?: JudgeEntered
  @observable routingInfo: RoutingInfo = {} as RoutingInfo
  @observable currentKey?: InputProfileFlowKey
  @observable closeUrl = "/next/profile"
  @observable enteredRate?: number
  editedPages: Array<InputProfileFlowKey> = []
  enteredPageCount = 0
  totalPageCount = 0

  fetchJudgeEntered = async () => {
    const result = await fetchGetStudentInputFlowJudgeEntered()
    if (!result.error && result.data) {
      runInAction(() => {
        this.totalPageCount = result.data["total_page_count"]
        this.enteredPageCount = result.data["entered_page_count"]
        this.enteredRate = result.data.entered_rate
        this.judgeEntered = new JudgeEntered({ ...result.data.judge_entered })
        this.routingInfo = this.createRoutingInfo()
        this.addPageCountBuffer()
      })
    }
  }

  @computed
  get currentRouting(): Routing {
    return this.routingInfo[this.currentKey || "done"]
  }

  @computed
  get isExistRoutingInfo(): boolean {
    return Object.keys(this.routingInfo).length !== 0
  }

  setCloseUrl = (closeUrl: string) => {
    runInAction(() => {
      this.closeUrl = closeUrl
    })
  }

  setCurrentKey = (pageName: InputProfileFlowKey) => {
    runInAction(() => {
      this.currentKey = pageName
    })
  }

  setEditedPages = (pageName: InputProfileFlowKey) => {
    if (!this.editedPages.includes(pageName)) {
      this.editedPages.push(pageName)
    }
  }

  createRoutingInfo = () => {
    return Object.entries(this.judgeEntered!)
      .map(([key, value]) => [this.convertToKey(key), value])
      .filter(([key, value]) => value === false || this.editedPages.includes(key))
      .map(([key, value], index, array) => {
        return {
          key: key,
          entered: value,
          page: index + 1,
          totalPage: array.length,
          nextPage: (array[index + 1] && array[index + 1][0]) || "done",
        }
      })
      .reduce((obj, data) => ({ ...obj, [data.key]: data }), {} as RoutingInfo)
  }

  convertToKey(key: string): InputProfileFlowKey {
    switch (key) {
      case "image":
        return "image"
      case "application_categories":
        return "activity"
      case "job_hunting_interest":
        return "interest"
      case "student_company_selections":
        return "selection"
      case "self_appeals":
        return "appeal"
      case "experiences":
        return "experience"
      default:
        return key as InputProfileFlowKey
    }
  }

  addPageCountBuffer = () => {
    if (!this.judgeEntered || !this.routingInfo) {
      return
    }
    if (this.judgeEntered.application_categories === false) {
      if (this.routingInfo.image) this.routingInfo.image.totalPage += 2
      if (this.routingInfo.activity) this.routingInfo.activity.totalPage += 2
    }
  }

  findEntryPage = () => {
    let entryPage = { key: "done", page: this.totalPageCount + 1 }
    Object.values(this.routingInfo)
      .filter((routing) => !routing.entered)
      .forEach((routing) => {
        if (routing.page < entryPage.page) entryPage = routing
      })
    return entryPage.key
  }

  clearEditedPages = () => {
    this.editedPages = []
  }
}
